// Lib
import React, { useCallback, useState } from 'react';
import 'cropperjs/dist/cropper.css';
import { useSelector } from 'react-redux';
import { inRange } from 'lodash/fp';

// Utils
import { getHeight, getWidth } from '../../../../../../common/maths/geometry/rect';
import { getElementId, getImageProp } from '../../../../../../common/elements/utils/elementPropertyUtils';
import getFilenameFromUrl from '../../../../../../common/files/getFilenameFromUrl';
import { appendToFilename } from '../../imageModalFileUtils';
import { ImageModalFlipDirection, trackImageModalFlip } from '../../content/utils/imageModalEditorAnalyticsUtils';
import { composeImageEditorTransforms } from '../../content/utils/imageModalEditorDataUtils';
import {
    getCropperDataFromImageEditorTransform,
    getCropToolToggleSession,
    getImageEditorTransformFromCropperData,
} from '../../content/utils/imageModalEditorDataAdapterUtils';
import { asSessionTransform } from '../../content/utils/imageModalTransformTypes';

// Selectors
import { getIsFeatureEnabledForCurrentUser } from '../../../../feature/elementFeatureSelector';

// Components
import ImageModalToolbarSecondaryTools from '../../toolbar/ImageModalToolbarSecondaryTools';
import ImageModalV2EditorSurface from './ImageModalV2EditorSurface';

// Constants
import { ExperimentId } from '../../../../../../common/experiments/experimentsConstants';

// Styles
import '../../content/ImageModalEditor.scss';

// Hooks
import { useCropSaveHandler } from '../../content/hooks/useCropSaveHandler';
import { useCropSourceSwapFootprint } from '../../content/hooks/useCropSourceSwapFootprint';
import { useImageEditorSaveFreeze } from '../../content/hooks/useImageEditorSaveFreeze';
import { useBackgroundRemovalWipeOverlay } from '../../content/hooks/useBackgroundRemovalWipeOverlay';
import { useBackgroundRemovalCrop } from '../../content/hooks/useBackgroundRemovalCrop';
import { useImageModalV2EditorTransformSession } from './hooks/useImageModalV2EditorTransformSession';
import { useImageModalV2EditorSources } from './hooks/useImageModalV2EditorSources';
import { useImageModalV2DrawingPreview } from './hooks/useImageModalV2DrawingPreview';
import { useImageModalV2EditorCropper } from './hooks/useImageModalV2EditorCropper';

// Types
import type Cropper from 'cropperjs';
import type { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

export type ImageModalV2EditorContentProps = {
    element: ImMNElement;
    gridSize: number;
    attachment?: unknown;
    // The available area the editor fits the source image into.
    editorBounds: {
        width: number;
        height: number;
    };
    cancelEditing: () => void;
    // `didSave` is true only when a changed image was persisted (so the caller can hold the frame until the
    // new source loads); a clean no-op close passes false to exit immediately.
    afterSaveCb: (didSave?: boolean, options?: { viewPreviewReady?: boolean }) => void;
    onSaveFreezeStart?: () => void;
    renderToolbar: (props: ImageModalV2EditorToolbarRenderProps) => React.ReactNode;
    editorIsReady: boolean;
    isAwaitingPreviewPaint?: boolean;
    editorContentRef?: React.RefObject<HTMLDivElement>;
    showImageReplacementWipe: boolean;
    showEditorLoadingPreview: boolean;
    toolbarClassName: string;
    onEditorReadyChange: (ready: boolean) => void;
    scheduleCroppedImageCreation: (createCroppedImage: () => void) => void;
};

export type ImageModalV2EditorToolbarRenderProps = {
    cropActive: boolean;
    hasChanges: boolean;
    ignoreActions: boolean;
    // The crop toggle only switches which source loads, so unlike the transform actions it stays
    // responsive while the editor is still loading — it is soft-blocked only mid source swap.
    ignoreCropToggle: boolean;
    onCropClick: () => void;
    onRotateClick: () => void;
    onFlipHorizontalClick: () => void;
    onFlipVerticalClick: () => void;
    onCancelClick: () => void;
    onSaveClick: () => void;
};

/**
 * Resolves the editor box footprint to pin while its displayed image is stale.
 *
 * The save freeze and the crop-tool source-swap pin both hold the editor box steady while a stale image
 * is still on screen — re-fitting it mid-swap flashes the modal background around the outgoing Cropper.
 * A save freeze always wins: once a save is in flight the frame it captured is the one being held, and it
 * only ever resolves via a full remount (see useImageEditorSaveFreeze), so there's nothing left for the
 * crop-swap pin to contribute in that window.
 */
const resolvePinnedEditorFootprint = (
    frozenEditorFootprint: { width: number; height: number } | null,
    pinnedEditorFootprint: { width: number; height: number } | null,
    liveFootprint: { width: number; height: number },
): { width: number; height: number } => frozenEditorFootprint ?? pinnedEditorFootprint ?? liveFootprint;

/**
 * V2 image editor content: crop/rotate/flip with a Background-removal-aware source toggle, a save-freeze
 * held frame, and a path-baked drawing preview. Rendered inside the V2 shell (see ImageModalV2Editor).
 *
 * This component only orchestrates; each concern lives in its own hook: which sources load
 * (useImageModalV2EditorSources), the rotate/flip session state (useImageModalV2EditorTransformSession),
 * the Cropper instance and its geometry effects (useImageModalV2EditorCropper), and the transformed
 * drawing overlay (useImageModalV2DrawingPreview).
 */
export const ImageModalV2EditorContent: React.FC<ImageModalV2EditorContentProps> = (props) => {
    const {
        element,
        gridSize,
        attachment,
        editorBounds,
        cancelEditing,
        afterSaveCb,
        onSaveFreezeStart,
        editorIsReady,
        isAwaitingPreviewPaint,
        editorContentRef,
        onEditorReadyChange,
        showEditorLoadingPreview,
        showImageReplacementWipe,
        toolbarClassName,
        renderToolbar,
        scheduleCroppedImageCreation,
    } = props;

    const useSecureMediaUrl = true;
    const elementId = getElementId(element);

    const renderSecureImagesThroughCF = useSelector(
        getIsFeatureEnabledForCurrentUser(ExperimentId.renderSecureImagesThroughCF),
    );

    const {
        backgroundRemovalEditSource,
        imageType,
        uploadEditedImage,
        createAdditionalCroppedImageBlob,
        uploadAdditionalCroppedImage,
        updateElementImageData,
        revertToOriginalImageData,
        getRevertImage,
    } = useBackgroundRemovalCrop({
        element,
        elementId,
        useSecureMediaUrl,
        renderSecureImagesThroughCF,
        // V2's plain edit upload publishes a local preview the modal view renders while it uploads, so the
        // board canvas holds the previous image until the upload completes (see hasOptimisticUploadPreview).
        markUploadAsEditPreview: true,
    });

    const [isCropToolSelected, setIsCropToolSelected] = useState(false);
    const shouldUseOriginalEditorSource = isCropToolSelected;
    const {
        pinnedEditorFootprint,
        isCropSourceSwapPending,
        beginCropSourceSwap,
        handleEditorReadyChange,
        consumePendingSwapReflow,
    } = useCropSourceSwapFootprint({ enabled: true, onEditorReadyChange });

    const session = useImageModalV2EditorTransformSession({ element, shouldUseOriginalEditorSource });

    const sources = useImageModalV2EditorSources({
        element,
        elementId,
        gridSize,
        attachment,
        editorBounds,
        backgroundRemovalEditSource,
        imageType,
        shouldUseOriginalEditorSource,
        isRotatedOnSide: session.isRotatedOnSide,
        initialRotation: session.initialRotation,
        initialScaleX: session.initialScaleX,
        initialScaleY: session.initialScaleY,
        useSecureMediaUrl,
        renderSecureImagesThroughCF,
    });

    // Hold the editor's rendered view stable across a save so the in-flight Redux updates don't make the
    // image flash or reload mid-save. See the hook for the full rationale.
    const {
        isFrozen,
        onSaveStart,
        displayElement,
        activeEditorImageLoadKey,
        activeLargestUneditedImageUrl,
        frozenEditorFootprint,
    } = useImageEditorSaveFreeze({
        enabled: true,
        element,
        editorImageLoadKey: sources.editorImageLoadKey,
        largestUneditedImageUrl: sources.largestUneditedImageUrl,
    });
    // The save freeze and the crop-tool source swap both pin the editor box: re-fitting it while the
    // displayed image is stale flashes the modal background around the outgoing Cropper.
    const { width: displayModalWidth, height: displayModalHeight } = resolvePinnedEditorFootprint(
        frozenEditorFootprint,
        pinnedEditorFootprint,
        { width: sources.modalWidth, height: sources.modalHeight },
    );

    const { hasDrawing, drawingPreviewElement, drawingViewBox } = useImageModalV2DrawingPreview({
        displayElement,
        shouldUseOriginalEditorSource,
        drawingSessionTransform: session.drawingSessionTransform,
        hasSessionDrawingTransform: session.hasSessionDrawingTransform,
    });

    const { handleImageDataUrlLoad, handleWipeAnimationEnd, wipeOverlay } = useBackgroundRemovalWipeOverlay({
        enabled: showImageReplacementWipe,
        editorImageLoadKey: activeEditorImageLoadKey,
        editorIsReady,
        imageTransform: session.editorImageTransform,
    });

    const cropper = useImageModalV2EditorCropper({
        element,
        elementId,
        imageType,
        largestUneditedImageUrl: sources.largestUneditedImageUrl,
        shouldUseOriginalEditorSource,
        isFrozen,
        editorIsReady,
        initialRotation: session.initialRotation,
        currentRotation: session.currentRotation,
        currentScaleX: session.currentScaleX,
        currentScaleY: session.currentScaleY,
        setCurrentRotation: session.setCurrentRotation,
        setCurrentScaleX: session.setCurrentScaleX,
        setCurrentScaleY: session.setCurrentScaleY,
        resetSessionToInitial: session.resetSessionToInitial,
        markEditorChanged: session.markEditorChanged,
        activeEditorImageLoadKey,
        activeLargestUneditedImageUrl,
        prefetchEditorSource: sources.prefetchEditorSource,
        onImageDataUrlLoad: handleImageDataUrlLoad,
        handleEditorReadyChange,
        consumePendingSwapReflow,
        displayModalWidth,
        displayModalHeight,
    });

    const handleSaveStart = useCallback(() => {
        onSaveStart({ width: sources.modalWidth, height: sources.modalHeight });
        onSaveFreezeStart?.();
    }, [sources.modalHeight, sources.modalWidth, onSaveFreezeStart, onSaveStart]);
    const getPersistedImageEditorData = useCallback(
        // Only wired up outside the crop tool, so the cropper is showing the edited render — its data is
        // an edited-image-relative session edit that composes onto the stored full-source transform.
        (sessionData: Cropper.Data): Cropper.Data =>
            getCropperDataFromImageEditorTransform(
                composeImageEditorTransforms(
                    session.storedTransform,
                    asSessionTransform(getImageEditorTransformFromCropperData(sessionData)),
                    {
                        width: sources.fullSourceImageWidth,
                        height: sources.fullSourceImageHeight,
                    },
                ),
            ),
        [sources.fullSourceImageHeight, sources.fullSourceImageWidth, session.storedTransform],
    );

    const { handleSave } = useCropSaveHandler({
        cropperRef: cropper.cropperRef,
        isDirtyRef: cropper.isDirtyRef,
        elementId,
        cropSourceImage: sources.fullSourceImage,
        imageType,
        largestUneditedImageUrl: sources.fullSourceImageUrl || sources.largestUneditedImageUrl,
        // V2 path-bakes the drawing through the session transform, so it is never hidden pending a save.
        hideDrawing: false,
        getFilenameFromUrl,
        appendToFilename,
        inRange,
        updateElementImageData,
        revertToOriginalImageData,
        uploadEditedImage,
        // Both paths upload the displayed render under the element's own attachment id, so its local preview
        // is already decoded when the save completes: the handoff finishes in the same batch as the element
        // update rather than waiting on a network fetch of the saved source.
        hasOptimisticUploadPreview: true,
        scheduleCroppedImageCreation,
        onSaveStart: handleSaveStart,
        shouldSaveWhenClean: false,
        getPersistedImageEditorData:
            shouldUseOriginalEditorSource || !session.storedTransform ? undefined : getPersistedImageEditorData,
        getRevertedImage: getRevertImage,
        createAdditionalCroppedImageBlob,
        uploadAdditionalCroppedImage,
        getDrawingSessionTransform: session.getDrawingSessionTransform,
        afterSaveCb,
    });

    // V2 path-bakes the drawing through the live session transform, so rotation applies immediately —
    // there is no need to warn and clear the drawing first, unlike V1.
    const handleRotateClick = () => {
        cropper.handleRotateImage();
        session.markEditorChanged();
    };

    const handleFlipHorizontalClick = () => {
        trackImageModalFlip(ImageModalFlipDirection.HORIZONTAL);
        cropper.handleFlipImageHorizontally();
        session.markEditorChanged();
    };

    const handleFlipVerticalClick = () => {
        trackImageModalFlip(ImageModalFlipDirection.VERTICAL);
        cropper.handleFlipImageVertically();
        session.markEditorChanged();
    };

    const toggleCropTool = () => {
        const enablingCropTool = !isCropToolSelected;

        // Hold the outgoing footprint (and hide the crop box/handles) until the swapped-in source has
        // replaced into Cropper: the other source usually has different dimensions, and even a same-URL
        // swap recomposes the session transform — either would re-fit the box around a stale image.
        beginCropSourceSwap({ width: displayModalWidth, height: displayModalHeight });

        const editedImage = getImageProp(element);
        const nextSession = getCropToolToggleSession({
            enablingCropTool,
            liveCropperData: cropper.getLiveCropperData(),
            storedTransform: session.storedTransform,
            fullSourceSize: { width: sources.fullSourceImageWidth, height: sources.fullSourceImageHeight },
            editedImageSize: {
                width: getWidth(editedImage) || sources.fullSourceImageWidth,
                height: getHeight(editedImage) || sources.fullSourceImageHeight,
            },
        });

        if (nextSession.pendingCropperData) cropper.setPendingCropperData(nextSession.pendingCropperData);
        session.applySessionTransform(nextSession);
        setIsCropToolSelected(enablingCropTool);
    };

    // A save in flight soft-blocks the toolbar rather than disabling it: the toolbar is already animating
    // out, so it should leave looking exactly as it did instead of flashing a disabled state on the way.
    // Interaction during the handful of frames before the exiting band turns off pointer events is
    // covered here.
    const ignoreToolbarActions = !editorIsReady || isCropSourceSwapPending || isFrozen;
    // A crop-tool press while the editor is still loading must not be dropped — it only switches which
    // source loads, so it works without a live Cropper. It is soft-blocked only mid swap or mid save.
    const ignoreCropToggle = isCropSourceSwapPending || isFrozen;

    return (
        <>
            <ImageModalV2EditorSurface
                element={element}
                editorContentRef={editorContentRef}
                imageRef={cropper.imageRef}
                isAwaitingPreviewPaint={isAwaitingPreviewPaint}
                showEditorLoadingPreview={showEditorLoadingPreview}
                isCropToolSelected={isCropToolSelected}
                isCropSourceSwapPending={isCropSourceSwapPending}
                displayModalWidth={displayModalWidth}
                displayModalHeight={displayModalHeight}
                isRotatedOnSide={session.isRotatedOnSide}
                loadingImageTransform={session.loadingImageTransform}
                useSecureMediaUrl={useSecureMediaUrl}
                displayElement={displayElement}
                cropSourceImage={sources.cropSourceImage}
                elementId={elementId}
                largestUneditedImageUrl={sources.largestUneditedImageUrl}
                drawingPreviewElement={drawingPreviewElement}
                drawingViewBox={drawingViewBox}
                hasDrawing={hasDrawing}
                editorIsReady={editorIsReady}
                showImageReplacementWipe={showImageReplacementWipe}
                wipeOverlay={wipeOverlay}
                onWipeAnimationEnd={handleWipeAnimationEnd}
            />
            <ImageModalToolbarSecondaryTools className={toolbarClassName}>
                <>
                    {renderToolbar({
                        cropActive: isCropToolSelected,
                        hasChanges: session.hasEditorChanges,
                        ignoreActions: ignoreToolbarActions,
                        ignoreCropToggle,
                        onCropClick: toggleCropTool,
                        onRotateClick: handleRotateClick,
                        onFlipHorizontalClick: handleFlipHorizontalClick,
                        onFlipVerticalClick: handleFlipVerticalClick,
                        onCancelClick: cancelEditing,
                        onSaveClick: handleSave,
                    })}
                </>
            </ImageModalToolbarSecondaryTools>
        </>
    );
};
